import { computed, Injectable, signal, Injector } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { catchError, map, Observable, tap, throwError, forkJoin, of } from "rxjs";
import { ResponseModel } from "../models/response.model";
import { UserInfoUpdate, UserModel } from "../models/user.model";
import { ConfigService } from "./config.service";
import { HistoryRequestModel } from "../models/history/history-request.model";
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { ErrorMapperService } from "./error-mapper.service";
import { MessageService } from './message.service';
import { environment } from "../../environments/environment";
import { AuthService } from './auth.service';


@Injectable({
  providedIn: 'root'
})
export class UserService {

  private userSignal = signal<UserModel | null>(null);
  user = this.userSignal.asReadonly();

  private balanceSignal = signal<number | null>(null);
  balance = this.balanceSignal.asReadonly();

  private compBalanceSignal = signal<number | null>(null);
  compBalance = this.compBalanceSignal.asReadonly();

  readonly currency = computed<string>(() => {
    const user = this.userSignal();
    if (!user) return 'USD'; //use default 'usd' in templates
    return user.currency
  })

  constructor(
    private http: HttpClient,
    private configs: ConfigService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router,
    private errorMapper: ErrorMapperService,
    private messageService: MessageService,
    private injector: Injector
  ) {
  }

  getUserInfo(): Observable<UserModel> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.info", "params": {} })
      .pipe(
        map(response => {
          if ('result' in response && 'user' in response.result) {
            return response.result.user as UserModel;
          }
          throw response;
        }),
        tap((user: UserModel) => this.updateUser(user)),
        catchError(error => throwError(() => error))
        // error handling by calling method (in authService)
      )
  }



  //editing user profile - title, phone, address, marketing preferences
  updateUserInfo(updatedInfo: UserInfoUpdate): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.setInfo", "params": { info: updatedInfo } })
      .pipe(
        map((response: ResponseModel) => {
          if ('result' in response) {
            return response;
          }
          throw response
        }),
        catchError(error => throwError(() => this.errorMapper.mapRegistrationError(error)))
        //find out to use this or simple API errors: main idea would be to use reg errors on user details
      )
  }

  //set user language on serverside - edit locale in user profile
  updateUserLocale(userLocale: string): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.setInfo", "params": { info: { locale: userLocale } } })
      .pipe(
        map((response: ResponseModel) => {
          if ('result' in response) {
            return response;
          }
          throw response;
        }),
        tap(() => {
          const user = this.userSignal();
          if (user) {
            user.locale = userLocale
          }
          this.updateUser(user);
        }),
        catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
        //we dont handle them, silently fails
      )
  }



  //changing user password
  changePassword(passwordData: { oldPassword: string, newPassword: string }): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.changePassword", "params": passwordData })
      .pipe(
        map((response: ResponseModel) => {
          if ('result' in response) {
            return response;
          }
          throw response;
        }),
        catchError(error => throwError(() => this.errorMapper.mapAPIerrors(error)))
      )
  }

  resendVerificationEmail(): Observable<{ result: {} }> {
    return this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "user.resendVerificationEmail", "params": {} })
      .pipe(
        map((response: ResponseModel) => {
          if ('result' in response) {
            return response;
          }
          throw response;
        }),
        catchError(error => throwError(() => this.errorMapper.mapAPIerrors(error)))
      )
  }

  //get transaction history
  getTransactionHistory(method: string, details: HistoryRequestModel, includeLegacy: boolean = true): Observable<any> {
    const currentRequest = this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": `cashier.history.${method}`, "params": details })
      .pipe(
        map((response: ResponseModel) => {
          if ('result' in response) {
            return response.result;
          }
          throw response;
        }),
        catchError(error => throwError(() => this.errorMapper.mapAPIerrors(error)))
      );

    let legacyToken: string | null = null;
    try {
      const authService = this.injector.get(AuthService);
      legacyToken = authService.legacyTokenSignal();
    } catch (e) {
      console.warn('Could not read from authService legacyTokenSignal', e);
    }

    if (!legacyToken) {
      try {
        legacyToken = localStorage.getItem('legacySessionToken');
      } catch (e) {
        console.warn('Error accessing localStorage in user.service:', e);
      }
    }

    if (legacyToken && includeLegacy) {
      const legacyApiUrl = environment.apiUrl ?? `https://www.casinocontroller.com/${environment.rivalName}/api-player/v3/jsonrpc`;
      const legacyCasinoId = environment.casinoId;

      const legacyRequest = this.http.post<ResponseModel>(legacyApiUrl, { "jsonrpc": "2.0", "id": legacyCasinoId, "method": `cashier.history.${method}`, "params": details }, {
        headers: {
          'Authorization': `Bearer ${legacyToken}`
        }
      }).pipe(
        map((response: ResponseModel) => {
          if ('result' in response) {
            return response.result;
          }
          return { [method]: [] };
        }),
        catchError(() => of({ [method]: [] }))
      );

      return forkJoin([currentRequest, legacyRequest]).pipe(
        map(([currentResult, legacyResult]) => {
          const mergedResult: any = { ...currentResult };
          if ((currentResult as any)[method] && (legacyResult as any)[method]) {
            mergedResult[method] = [...(currentResult as any)[method], ...(legacyResult as any)[method]];
            // Sort descending by ts
            mergedResult[method].sort((a: any, b: any) => {
              const dateA = new Date(a.ts || 0).getTime();
              const dateB = new Date(b.ts || 0).getTime();
              return dateB - dateA;
            });
          }
          return mergedResult;
        })
      );
    }

    return currentRequest;
  }


  refreshBalance() {
    this.http.post<ResponseModel>(`${this.configs.apiUrl}`, { "jsonrpc": "2.0", "id": this.configs.casinoId, "method": "session.keepAlive", "params": {} })
      .pipe(
        map(response => {
          if ('result' in response && 'balance' in response.result) {
            return {
              balance: response.result.balance as number,
              compBalance: (response.result as any).compBalance as number | undefined,
              hasUnreadMessage: (response.result as any).hasUnreadMessage as boolean | undefined
            };
          }
          throw response;
        }),
        catchError((error) => throwError(() => this.errorMapper.mapAPIerrors(error)))
      ).subscribe({
        next: (res) => {
          this.updateBalance(res.balance);
          if (res.compBalance !== undefined) {
            this.updateCompBalance(res.compBalance);
          }
          if (res.hasUnreadMessage !== undefined) {
            this.messageService.updateHasUnreadSignal(res.hasUnreadMessage);
          }
        },
        error: (err) => {
          //Let the global keep alive handle errors, this is just a quick refresh call
          console.debug('Error on balance refresh', err);
        }
      })
  }


  /** warn user if the email is not verified */
  handleEmailVerification(user: UserModel) {
    // if(user.emailStatus === "UNCONFIRMED" && this.configs.isRivalCasino){ //only on rival brands
    if (user.emailStatus !== "UNCONFIRMED") return;

    const message = this.translate.instant('ACCOUNT.INFO.EMAIL_VERIFICATION.WARNING_MESSAGE');
    const title = this.translate.instant('ACCOUNT.INFO.EMAIL_VERIFICATION.WARNING_H1');
    const toast = this.toastr.error(message, title,
      {
        toastClass: 'ngx-toastr lobbyless-toast clickable-toast',
        timeOut: 0, // stay until clicked
        extendedTimeOut: 0, // stays even on hover
        closeButton: true
      }
    );

    toast.onTap.subscribe(() => {
      this.router.navigate(['account', 'profile', 'email-verification']);
    });

  }


  updateUser(user: UserModel | null): void {
    this.userSignal.set(user);
    this.updateCompBalance(user ? user.compBalance ?? null : null); //todo test in user-details component
  }

  updateBalance(balance: number | null): void {
    this.balanceSignal.set(balance);
  }

  updateCompBalance(compBalance: number | null): void {
    this.compBalanceSignal.set(compBalance);
  }
}
